// src/admin/AdminCEOAgent.js
// Aria — CoupleCart AI CEO Agent
// Calls Firebase Cloud Function proxy (functions/index.js) — API key stays on server

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Send, Zap, TrendingUp, ShoppingBag, Users,
  Tag, Lightbulb, CheckSquare, Square, RefreshCw,
  BarChart2, Brain, Sparkles, ChevronRight,
} from 'lucide-react';
import { collection, getDocs } from 'firebase/firestore';
import { db, auth } from '../firebase/config';
import { useApp } from '../context/AppContext';

// ─── Your Firebase project region + project ID ─────────────────────
// Update REGION and PROJECT_ID to match yours
const REGION     = 'asia-south1';
const PROJECT_ID = process.env.REACT_APP_FIREBASE_PROJECT_ID || 'couplecart-259d2';
const PROXY_URL  = `https://${REGION}-${PROJECT_ID}.cloudfunctions.net/ariaAgent`;

const SYSTEM_PROMPT = `You are Aria, an expert AI CEO Agent for CoupleCart — an Indian handmade and personalized gift e-commerce website targeting couples, families, and relationships in India.

CoupleCart facts:
- Sells: personalized gifts, handmade items, surprise boxes, anniversary gifts, Valentine's gifts, jewellery, clothing, home decor, food & sweets, cards & stationery, experience gifts
- Recipients: Girlfriend, Boyfriend, Wife, Husband, Female Friend, Male Friend, Sister, Brother, Mother, Father, Couples, Anniversary
- Tech: React.js, Firebase, Razorpay, Shiprocket, EmailJS
- Market: India. Price range: ₹100–₹5000
- Domain: couplecart.in

Your role as CEO Agent:
- Act as a strategic C-suite advisor who deeply understands the Indian gift market
- Give specific, actionable advice tailored to Indian e-commerce
- Indian festivals = HUGE opportunities: Diwali, Rakhi, Valentine's Day, Karwa Chauth, Mothers Day, Friendship Day
- When asked for insights, format as: [EMOJI] Title | One line insight | Action item
- Always use ₹ for prices. Reference Indian platforms: WhatsApp Business, Instagram Reels, Meesho, Flipkart, Amazon.in
- COD (Cash on Delivery) is critical for Indian tier-2 and tier-3 cities
- WhatsApp is India's #1 customer communication channel
- Be concise, confident, strategic. No fluff. CEO-level useful every sentence.
- Mix Hindi words naturally when appropriate
- Think seasonality: Oct–Feb is peak gifting season in India

When you generate insights, always format as:
[EMOJI] Title | One line insight | Specific action item`;

// ─── Call the Firebase proxy function ─────────────────────────────
async function callAria(messages) {
  const currentUser = auth.currentUser;
  if (!currentUser) throw new Error('Not logged in');

  // Force-refresh token
  const idToken = await currentUser.getIdToken(true);

  let response;
  try {
    response = await fetch(PROXY_URL, {
      method:  'POST',
      headers: {
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${idToken}`,
      },
      body: JSON.stringify({ messages, systemPrompt: SYSTEM_PROMPT }),
    });
  } catch (networkErr) {
    throw new Error('Network error: ' + networkErr.message);
  }

  const text = await response.text();
  let parsed = {};
  try { parsed = JSON.parse(text); } catch {}

  if (!response.ok) {
    const detail = parsed.details || parsed.error || text || `HTTP ${response.status}`;
    console.error('Proxy error:', response.status, detail);
    throw new Error(`[${response.status}] ${detail}`);
  }

  return parsed.content || 'I had trouble responding. Please try again.';
}

// ─── Parse insight lines ───────────────────────────────────────────
function parseInsights(text) {
  const results = [];
  for (const line of text.split('\n')) {
    const m = line.trim().match(/^(.+?)\s*\|\s*(.+?)\s*\|\s*(.+)$/);
    if (m) results.push({ title: m[1].trim(), body: m[2].trim(), action: m[3].trim() });
  }
  return results;
}

// ─── Sub-components ────────────────────────────────────────────────
function TaskItem({ text, priority, done: init }) {
  const [done, setDone] = useState(init || false);
  const colors = { high: 'bg-red-100 text-red-700', medium: 'bg-amber-100 text-amber-700', low: 'bg-green-100 text-green-700' };
  return (
    <div onClick={() => setDone(!done)}
      className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-all hover:bg-gray-50 ${done ? 'opacity-50' : ''}`}>
      {done
        ? <CheckSquare size={16} className="text-green-500 shrink-0" />
        : <Square      size={16} className="text-gray-300 shrink-0" />
      }
      <span className={`flex-1 font-body text-sm ${done ? 'line-through text-gray-400' : 'text-gray-700'}`}>{text}</span>
      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full shrink-0 ${colors[priority] || colors.medium}`}>{priority}</span>
    </div>
  );
}

function InsightCard({ title, body, action }) {
  const emojiMatch = title.match(/^(\p{Emoji}[\p{Emoji}\s]*)\s*(.+)$/u);
  const emoji      = emojiMatch ? emojiMatch[1].trim() : '💡';
  const cleanTitle = emojiMatch ? emojiMatch[2].trim() : title;
  return (
    <div className="bg-white border border-gray-100 rounded-2xl p-4 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 bg-rose-50 rounded-xl flex items-center justify-center text-lg shrink-0">{emoji}</div>
        <div className="flex-1 min-w-0">
          <p className="font-body font-semibold text-gray-900 text-sm">{cleanTitle}</p>
          <p className="font-body text-gray-500 text-xs mt-0.5 leading-relaxed">{body}</p>
          {action && (
            <div className="mt-2 flex items-center gap-1.5 text-rose-600 bg-rose-50 rounded-lg px-2.5 py-1.5">
              <ChevronRight size={12} />
              <span className="font-body text-xs font-semibold">{action}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-gray-400 font-body text-xs">{label}</span>
        <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${color}`}>
          <Icon size={15} className="text-white" />
        </div>
      </div>
      <p className="font-display font-bold text-xl text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 font-body mt-0.5">{sub}</p>}
    </div>
  );
}

// ─── Main ─────────────────────────────────────────────────────────
export default function AdminCEOAgent() {
  const { user } = useApp();

  const [messages,  setMessages]  = useState([{
    role: 'assistant',
    content: `Namaste! 🙏 I'm Aria, your CoupleCart AI CEO Agent.\n\nI'm here to help you grow CoupleCart into India's most loved gift brand. I can analyze your business, suggest strategies for Indian festivals, recommend pricing, generate coupon ideas, and create actionable growth plans.\n\nTell me your current situation — orders this month, best-selling products, biggest challenges — and I'll give you CEO-level insights. What would you like to tackle first?`,
  }]);
  const [input,     setInput]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [insights,  setInsights]  = useState([]);
  const [stats,     setStats]     = useState({ orders: 0, revenue: 0, merchants: 0, products: 0 });
  const [history,   setHistory]   = useState([]);
  const [activeTab, setActiveTab] = useState('chat');
  const [error,     setError]     = useState('');

  const chatRef  = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    if (chatRef.current) chatRef.current.scrollTop = chatRef.current.scrollHeight;
  }, [messages, loading]);

  // Load stats from Firestore
  useEffect(() => {
    (async () => {
      try {
        const [ordersSnap, merchantsSnap, productsSnap] = await Promise.all([
          getDocs(collection(db, 'orders')),
          getDocs(collection(db, 'merchants')),
          getDocs(collection(db, 'products')),
        ]);
        const orders  = ordersSnap.docs.map((d) => d.data());
        const revenue = orders.reduce((s, o) => s + (o.total || 0), 0);
        setStats({ orders: orders.length, revenue, merchants: merchantsSnap.size, products: productsSnap.size });
      } catch (e) { console.error(e); }
    })();
  }, []);

  const sendMessage = useCallback(async (text) => {
    const msg = (text || input).trim();
    if (!msg || loading) return;
    setInput('');
    setError('');

    const userMsg    = { role: 'user', content: msg };
    const newHistory = [...history, userMsg];

    setMessages((prev) => [...prev, userMsg]);
    setHistory(newHistory);
    setLoading(true);

    try {
      const reply   = await callAria(newHistory);
      const asstMsg = { role: 'assistant', content: reply };
      setMessages((prev) => [...prev, asstMsg]);
      setHistory((prev) => [...prev, asstMsg]);

      // Auto-parse insights
      const parsed = parseInsights(reply);
      if (parsed.length > 0) setInsights((prev) => [...parsed, ...prev].slice(0, 12));

    } catch (err) {
      console.error('Aria error:', err);
      const errMsg = err.message?.includes('Not logged in')
        ? 'Please log in to use Aria.'
        : err.message?.includes('Failed to fetch') || err.message?.includes('NetworkError')
          ? '⚠️ Cannot reach the server. Make sure the Firebase Cloud Function is deployed (see setup below).'
          : `⚠️ ${err.message || 'Something went wrong. Please try again.'}`;
      setError(errMsg);
      setMessages((prev) => [...prev, { role: 'assistant', content: errMsg }]);
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  }, [input, loading, history]);

  const quickAsk = (prompt) => sendMessage(prompt);

  const QUICK_PROMPTS = [
    { label: '📈 30-day growth plan',   prompt: `My CoupleCart store has ${stats.orders} orders and ${stats.products} products. Give me a specific 30-day growth plan to get to 100 orders in the Indian handmade gift market.` },
    { label: '🎪 Festival strategy',    prompt: 'Which Indian festivals should I focus on for CoupleCart this year and what specific product bundles should I create for each?' },
    { label: '💰 Pricing for India',    prompt: 'What is the ideal pricing strategy for handmade gifts on CoupleCart for Indian customers including tier-2 and tier-3 cities?' },
    { label: '🎟️ Coupon ideas',         prompt: 'Create 6 clever coupon codes for CoupleCart with Hindi-inspired names. Include when to use each, discount amount, and expected impact.' },
    { label: '📱 Instagram strategy',   prompt: 'Create a 4-week Instagram Reels content calendar for CoupleCart targeting Indian couples aged 18-35.' },
    { label: '🚀 Revenue opportunities',prompt: 'What are the top 5 untapped revenue opportunities for CoupleCart right now? Format as insights with emoji.' },
    { label: '🏆 Beat competition',     prompt: 'How should CoupleCart differentiate from Ferns N Petals, IGP, and Amazon India in the gift market?' },
    { label: '📊 Generate insights',    prompt: `CoupleCart has ${stats.orders} orders, ₹${stats.revenue.toLocaleString('en-IN')} revenue, ${stats.products} products. Generate 6 strategic CEO insights. Format: [EMOJI] Title | One line insight | Specific action item` },
  ];

  const DEFAULT_TASKS = [
    { text: 'Complete Razorpay KYC verification',    priority: 'high',   done: false },
    { text: 'Add 10+ real products with good photos', priority: 'high',   done: false },
    { text: 'Create WELCOME10 new-user coupon',       priority: 'high',   done: false },
    { text: 'Set up EmailJS notifications',           priority: 'high',   done: true  },
    { text: 'Configure Shiprocket pickup location',   priority: 'high',   done: true  },
    { text: 'Deploy Aria Firebase Cloud Function',    priority: 'high',   done: false },
    { text: 'Create Instagram Business page',         priority: 'medium', done: false },
    { text: 'Submit to Google Search Console',        priority: 'medium', done: false },
    { text: 'Set up WhatsApp Business account',       priority: 'medium', done: false },
    { text: 'Write Hindi product descriptions',       priority: 'medium', done: false },
    { text: 'Create Diwali gift bundle collections',  priority: 'low',    done: false },
    { text: 'Push code to GitHub for backup',         priority: 'low',    done: false },
  ];

  const TABS = [
    { id: 'chat',     label: 'Chat with Aria',  icon: Brain       },
    { id: 'insights', label: 'CEO Insights',     icon: Lightbulb   },
    { id: 'tasks',    label: 'Task Board',       icon: CheckSquare },
    { id: 'setup',    label: 'Setup Guide',      icon: Zap         },
  ];

  return (
    <div className="space-y-5 max-w-5xl">

      {/* Header */}
      <div className="bg-gradient-to-r from-rose-500 to-rose-700 rounded-3xl p-6 text-white relative overflow-hidden">
        <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/10 rounded-full" />
        <div className="absolute right-16 -bottom-4 w-24 h-24 bg-white/5 rounded-full" />
        <div className="relative z-10 flex items-center gap-4">
          <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center text-2xl border border-white/25">
            🤖
          </div>
          <div>
            <div className="flex items-center gap-2 mb-0.5">
              <h2 className="font-display font-bold text-xl">Aria — CEO Agent</h2>
              <span className="flex items-center gap-1 bg-green-400/20 border border-green-400/30 text-green-200 text-xs font-bold px-2.5 py-0.5 rounded-full">
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                Powered by Claude
              </span>
            </div>
            <p className="text-rose-200 font-body text-sm">
              AI business intelligence · Free · Powered by Gemini
            </p>
          </div>
        </div>
      </div>

      {/* Live stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={TrendingUp}  label="Total Revenue"   value={`₹${stats.revenue.toLocaleString('en-IN')}`} sub="All time"  color="bg-rose-500"   />
        <StatCard icon={ShoppingBag} label="Total Orders"    value={stats.orders}                                  sub="All time"  color="bg-blue-500"   />
        <StatCard icon={Users}        label="Merchants"       value={stats.merchants}                               sub="Active"    color="bg-purple-500" />
        <StatCard icon={BarChart2}    label="Products Listed" value={stats.products}                                sub="In store"  color="bg-green-500"  />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-white rounded-2xl p-1 border border-gray-100 shadow-sm">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setActiveTab(id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-xl text-xs font-body font-semibold transition-all ${
              activeTab === id ? 'bg-rose-500 text-white shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}>
            <Icon size={14} /><span className="hidden sm:inline">{label}</span>
          </button>
        ))}
      </div>

      {/* ── CHAT TAB ── */}
      {activeTab === 'chat' && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div ref={chatRef} className="flex flex-col gap-4 p-5 overflow-y-auto"
            style={{ minHeight: 380, maxHeight: 480 }}>
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-3 items-start ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm shrink-0 ${
                  msg.role === 'user' ? 'bg-gray-200 text-gray-600' : 'bg-rose-500 text-white'
                }`}>
                  {msg.role === 'user' ? '👤' : '🤖'}
                </div>
                <div className={`max-w-[78%] flex flex-col gap-1 ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                  <div className={`px-4 py-3 rounded-2xl font-body text-sm leading-relaxed whitespace-pre-wrap ${
                    msg.role === 'user'
                      ? 'bg-rose-500 text-white rounded-tr-sm'
                      : 'bg-gray-50 text-gray-800 rounded-tl-sm border border-gray-100'
                  }`}>
                    {msg.content}
                  </div>
                </div>
              </div>
            ))}

            {loading && (
              <div className="flex gap-3 items-start">
                <div className="w-8 h-8 rounded-full bg-rose-500 text-white flex items-center justify-center text-sm shrink-0">🤖</div>
                <div className="bg-gray-50 border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
                  <div className="flex items-center gap-1.5">
                    <div className="flex gap-1">
                      {[0, 1, 2].map((i) => (
                        <div key={i} className="w-2 h-2 bg-rose-400 rounded-full animate-bounce"
                          style={{ animationDelay: `${i * 0.15}s` }} />
                      ))}
                    </div>
                    <span className="text-xs text-gray-400 font-body ml-1">Aria is thinking...</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Quick prompts */}
          <div className="px-5 pb-3 border-t border-gray-100 pt-3">
            <p className="text-xs text-gray-400 font-body font-semibold uppercase tracking-wider mb-2">Quick Asks</p>
            <div className="flex flex-wrap gap-2">
              {QUICK_PROMPTS.map((q) => (
                <button key={q.label} onClick={() => quickAsk(q.prompt)} disabled={loading}
                  className="text-xs font-body px-3 py-1.5 rounded-xl bg-gray-50 hover:bg-rose-50 text-gray-600 hover:text-rose-600 border border-gray-200 hover:border-rose-300 transition-all disabled:opacity-50">
                  {q.label}
                </button>
              ))}
            </div>
          </div>

          {/* Input */}
          <div className="px-5 pb-5 pt-2">
            <div className="flex gap-2">
              <input ref={inputRef} type="text" value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
                placeholder="Ask Aria anything about your business..."
                className="flex-1 px-4 py-3 rounded-2xl border border-gray-200 focus:outline-none focus:border-rose-400 font-body text-sm bg-gray-50 focus:bg-white transition-all"
                disabled={loading}
              />
              <button onClick={() => sendMessage()} disabled={loading || !input.trim()}
                className="flex items-center gap-2 bg-rose-500 hover:bg-rose-600 text-white px-5 py-3 rounded-2xl font-body font-semibold text-sm transition-all shadow-[0_4px_12px_rgba(244,63,94,0.3)] disabled:opacity-50 shrink-0">
                <Send size={16} /> Ask
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── INSIGHTS TAB ── */}
      {activeTab === 'insights' && (
        <div className="space-y-4">
          {insights.length === 0 ? (
            <div className="bg-white rounded-3xl border border-gray-100 shadow-sm p-12 text-center">
              <Lightbulb size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="font-body font-medium text-gray-500 mb-1">No insights yet</p>
              <p className="font-body text-gray-400 text-sm mb-4">
                Ask Aria to "Generate CEO insights" and they'll appear here automatically.
              </p>
              <button
                onClick={() => { setActiveTab('chat'); quickAsk(`CoupleCart has ${stats.orders} orders, ₹${stats.revenue.toLocaleString('en-IN')} revenue, ${stats.products} products. Generate 6 strategic CEO insights. Format: [EMOJI] Title | One line insight | Specific action item`); }}
                className="inline-flex items-center gap-2 bg-rose-500 text-white px-6 py-3 rounded-2xl font-body font-semibold text-sm hover:bg-rose-600 transition-all">
                <Sparkles size={16} /> Generate Insights Now
              </button>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <p className="font-body text-sm text-gray-500">{insights.length} insights from Aria</p>
                <button
                  onClick={() => { setActiveTab('chat'); quickAsk('Generate 6 fresh CEO insights for CoupleCart. Format: [EMOJI] Title | One line insight | Specific action item'); }}
                  className="flex items-center gap-1.5 text-xs text-rose-500 font-body font-semibold border border-rose-200 px-3 py-1.5 rounded-xl hover:bg-rose-50 transition-all">
                  <RefreshCw size={13} /> Refresh
                </button>
              </div>
              <div className="grid gap-3">
                {insights.map((ins, i) => <InsightCard key={i} {...ins} />)}
              </div>
            </>
          )}
        </div>
      )}

      {/* ── TASKS TAB ── */}
      {activeTab === 'tasks' && (
        <div className="bg-white rounded-3xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between p-5 border-b border-gray-100">
            <div>
              <h3 className="font-display font-semibold text-gray-900">CEO Task Board</h3>
              <p className="text-xs text-gray-400 font-body mt-0.5">Click any task to mark it complete</p>
            </div>
            <button onClick={() => { setActiveTab('chat'); quickAsk(`Generate 5 high-priority tasks for CoupleCart CEO this week. We have ${stats.orders} orders and ${stats.products} products.`); }}
              className="flex items-center gap-1.5 text-xs text-rose-500 font-body font-semibold border border-rose-200 px-3 py-2 rounded-xl hover:bg-rose-50 transition-all">
              <Zap size={13} /> AI Generate Tasks
            </button>
          </div>
          <div className="p-4 divide-y divide-gray-50">
            {DEFAULT_TASKS.map((task, i) => <TaskItem key={i} {...task} />)}
          </div>
        </div>
      )}

      {/* ── SETUP TAB ── */}
      {activeTab === 'setup' && (
        <div className="space-y-4">
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-5">
            <p className="font-body font-bold text-amber-800 mb-1">⚠️ One-time setup required</p>
            <p className="font-body text-amber-700 text-sm">
              Aria uses a Firebase Cloud Function as a secure backend proxy so your Anthropic API key is never exposed to the browser. Follow these steps once.
            </p>
          </div>

          {[
            {
              step: '1',
              title: 'Get Anthropic API Key',
              color: 'bg-blue-50 border-blue-200',
              content: (
                <div className="space-y-2 text-sm font-body text-gray-700">
                  <p>Go to <strong>aistudio.google.com</strong> → Get API Key → Create API key in new project</p>
                  <p>Copy the key — it starts with <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs">AIzaSy</code></p>
                  <p className="text-green-700 font-semibold">✅ 100% Free — no credit card needed! 1500 requests/day free.</p>
                </div>
              ),
            },
            {
              step: '2',
              title: 'Install Firebase CLI tools',
              color: 'bg-purple-50 border-purple-200',
              content: (
                <div className="space-y-2">
                  <pre className="bg-gray-900 text-green-400 text-xs rounded-xl p-3 overflow-x-auto font-mono">{`npm install -g firebase-tools
firebase login
cd your-couplecart-folder
firebase init functions   # select JavaScript, no ESLint`}</pre>
                </div>
              ),
            },
            {
              step: '3',
              title: 'Store your API key as a secret',
              color: 'bg-rose-50 border-rose-200',
              content: (
                <div className="space-y-2">
                  <pre className="bg-gray-900 text-green-400 text-xs rounded-xl p-3 overflow-x-auto font-mono">{`firebase functions:secrets:set GEMINI_API_KEY
# Paste your AIzaSy... key when prompted`}</pre>
                  <p className="text-xs text-gray-500 font-body">✅ Key is encrypted — never in your code or GitHub</p>
                </div>
              ),
            },
            {
              step: '4',
              title: 'Copy the function files & deploy',
              color: 'bg-green-50 border-green-200',
              content: (
                <div className="space-y-2 text-sm font-body text-gray-700">
                  <p>Copy <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs">functions/index.js</code> and <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs">functions/package.json</code> from the zip into your project's <code className="bg-gray-100 px-1.5 py-0.5 rounded font-mono text-xs">functions/</code> folder, then:</p>
                  <pre className="bg-gray-900 text-green-400 text-xs rounded-xl p-3 overflow-x-auto font-mono">{`cd functions && npm install
firebase deploy --only functions
# Takes ~2 minutes`}</pre>
                </div>
              ),
            },
            {
              step: '5',
              title: 'Enable Cloud Functions billing',
              color: 'bg-amber-50 border-amber-200',
              content: (
                <div className="space-y-1 text-sm font-body text-gray-700">
                  <p>Firebase requires <strong>Blaze (pay-as-you-go)</strong> plan for Cloud Functions.</p>
                  <p>Go to <strong>Firebase Console → Upgrade Plan</strong>. Add a card.</p>
                  <p className="text-green-700 font-semibold">✅ Free tier covers 2 million calls/month — Aria won't cost you anything at your scale!</p>
                </div>
              ),
            },
          ].map(({ step, title, color, content }) => (
            <div key={step} className={`border rounded-2xl p-5 ${color}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-7 h-7 bg-rose-500 text-white rounded-full flex items-center justify-center text-xs font-bold shrink-0">{step}</div>
                <h3 className="font-body font-semibold text-gray-900">{title}</h3>
              </div>
              {content}
            </div>
          ))}

          <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
            <p className="text-green-800 font-body font-bold mb-1">🎉 Once deployed, Aria will work instantly!</p>
            <p className="text-green-700 font-body text-sm">Come back to the Chat tab and ask her your first question.</p>
          </div>
        </div>
      )}
    </div>
  );
}
